import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import React from 'react';
import { Image } from 'react-native';
import styled from 'styled-components/native';
import PlusStrokeIcon from '@/assets/icon/ic_plus_stroke.svg';
import { SccPressable } from '@/components/SccPressable';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { doorTypeMap } from '@/constant/options';
import { BuildingDoorDirectionTypeDto, PlaceDoorDirectionTypeDto, StairInfo, } from '@/generated-sources/openapi';
import { getFloorAccessibility, getPlaceEntranceStepType, getBuildingEntranceStepType, getBuildingElevatorType, getStairDescription, EntranceStepType, ElevatorType, } from './PlaceInfo.utils';
// ──────────────── InfoRow ────────────────
export function InfoRow({ label, value, subValue, }) {
    return (_jsxs(InfoRowContainer, { children: [_jsx(InfoLabel, { children: label }), _jsxs(InfoValueContainer, { children: [_jsx(InfoValue, { children: value }), subValue ? _jsx(InfoSubValue, { children: subValue }) : null] })] }));
}
// ──────────────── PhotoRow ────────────────
export function PhotoRow({ images }) {
    const navigation = useNavigation();
    if (images.length === 0) {
        return null;
    }
    const onPressImage = (index) => {
        navigation.navigate('ImageZoomViewer', {
            imageUrls: images.map(img => img.imageUrl),
            index,
        });
    };
    return (_jsx(PhotoRowContainer, { children: images.slice(0, 3).map((img, index) => (_jsx(SccPressable, { elementName: "accessibility_photo", logParams: { image_index: index.toString() }, onPress: () => onPressImage(index), children: _jsx(PhotoThumbnail, { source: { uri: img.imageUrl } }) }, index))) }));
}
// ──────────────── CommentBox ────────────────
export function CommentBox({ comments, registeredUserName, }) {
    var _a, _b, _c;
    const latestComment = comments[0];
    if (!latestComment) {
        return null;
    }
    const userName = (_c = (_b = (_a = latestComment.user) === null || _a === void 0 ? void 0 : _a.nickname) !== null && _b !== void 0 ? _b : registeredUserName) !== null && _c !== void 0 ? _c : '익명';
    const dateStr = dayjs(latestComment.createdAt.value).format('YYYY.MM.DD');
    return (_jsxs(CommentContainer, { children: [_jsx(CommentText, { children: latestComment.comment }), _jsxs(CommentMetaRow, { children: [_jsx(CommentUserName, { children: userName }), _jsx(CommentDate, { children: dateStr })] })] }));
}
// ──────────────── FieldCommentBox ────────────────
export function FieldCommentBox({ comment, userName, createdAt, }) {
    const dateStr = createdAt ? dayjs(createdAt.value).format('YYYY.MM.DD') : '';
    return (_jsxs(CommentContainer, { children: [_jsx(CommentText, { children: comment }), _jsxs(CommentMetaRow, { children: [_jsx(CommentUserName, { children: userName !== null && userName !== void 0 ? userName : '익명' }), dateStr ? _jsx(CommentDate, { children: dateStr }) : null] })] }));
}
// ──────────────── Building Info Rows ────────────────
export function BuildingEntranceInfoRows({ buildingAccessibility, }) {
    const entranceStepType = getBuildingEntranceStepType(buildingAccessibility);
    const stairInfo = buildingAccessibility.entranceStairInfo;
    const stairHeight = buildingAccessibility.entranceStairHeightLevel;
    const description = getStairDescription(stairHeight, stairInfo);
    let title = '';
    switch (entranceStepType) {
        case EntranceStepType.Flat:
            title = '계단, 경사로 없음';
            break;
        case EntranceStepType.SlopeOnly:
            title = '경사로 있음';
            break;
        case EntranceStepType.StairAndSlope:
            title = '계단과 경사로 있음';
            break;
        case EntranceStepType.StairOnly:
            title = '계단 있음';
            break;
    }
    return _jsx(InfoRow, { label: "\uC785\uAD6C \uC815\uBCF4", value: title, subValue: description });
}
export function BuildingElevatorInfoRow({ buildingAccessibility, }) {
    if (!buildingAccessibility)
        return null;
    const elevatorType = getBuildingElevatorType(buildingAccessibility);
    const stairInfo = buildingAccessibility.elevatorStairInfo;
    const stairHeight = buildingAccessibility.elevatorStairHeightLevel;
    const description = getStairDescription(stairHeight, stairInfo);
    let title = '';
    switch (elevatorType) {
        case ElevatorType.ElevatorAfterStair: {
            const hasStairs = stairInfo !== undefined && stairInfo !== StairInfo.None;
            const hasSlope = buildingAccessibility.elevatorHasSlope === true;
            if (hasStairs && hasSlope) {
                title = '엘리베이터 있지만,\n가는 길에 계단과 경사로 있음';
            }
            else if (hasSlope) {
                title = '엘리베이터 있지만,\n가는 길에 경사로 있음';
            }
            else {
                title = '엘리베이터 있지만,\n가는 길에 계단 있음';
            }
            break;
        }
        case ElevatorType.ElevatorNoBarriers:
            title = '엘리베이터 있음';
            break;
        case ElevatorType.NoElevator:
            title = '엘리베이터 없음';
            break;
    }
    return (_jsx(InfoRow, { label: "\uC5D8\uB9AC\uBCA0\uC774\uD130 \uC815\uBCF4", value: title, subValue: description }));
}
export function BuildingDoorInfoRow({ buildingAccessibility, }) {
    var _a;
    const doorTypes = (_a = buildingAccessibility.entranceDoorTypes) !== null && _a !== void 0 ? _a : [];
    if (doorTypes.length === 0) {
        return null;
    }
    const title = doorTypes.map(d => doorTypeMap[d]).join(', ');
    return _jsx(InfoRow, { label: "\uCD9C\uC785\uBB38 \uC720\uD615", value: title });
}
export function BuildingDoorDirectionInfoRow({ buildingAccessibility, }) {
    var _a;
    const doorDir = buildingAccessibility.doorDirectionType;
    if (!doorDir) {
        return null;
    }
    let title = '';
    let subValue;
    switch (doorDir) {
        case BuildingDoorDirectionTypeDto.RoadDirection:
            title = '지상/보도 연결 문';
            break;
        case BuildingDoorDirectionTypeDto.ParkingDirection:
            title = '주차장 방향';
            break;
        case BuildingDoorDirectionTypeDto.Etc:
            title = '기타';
            subValue = (_a = buildingAccessibility.doorDirectionEtcComment) !== null && _a !== void 0 ? _a : undefined;
            break;
    }
    return _jsx(InfoRow, { label: "\uCD9C\uC785\uAD6C \uC704\uCE58", value: title, subValue: subValue });
}
// ──────────────── Place Info Rows ────────────────
export function PlaceEntranceInfoRows({ placeAccessibility, }) {
    const entranceStepType = getPlaceEntranceStepType(placeAccessibility);
    const stairInfo = placeAccessibility.stairInfo;
    const stairHeight = placeAccessibility.stairHeightLevel;
    const description = getStairDescription(stairHeight, stairInfo);
    let title = '';
    switch (entranceStepType) {
        case EntranceStepType.Flat:
            title = '계단, 경사로 없음';
            break;
        case EntranceStepType.SlopeOnly:
            title = '경사로 있음';
            break;
        case EntranceStepType.StairAndSlope:
            title = '계단과 경사로 있음';
            break;
        case EntranceStepType.StairOnly:
            title = '계단 있음';
            break;
    }
    return _jsx(InfoRow, { label: "\uC785\uAD6C \uC815\uBCF4", value: title, subValue: description });
}
export function PlaceDoorInfoRow({ placeAccessibility, }) {
    var _a;
    const doorTypes = (_a = placeAccessibility.entranceDoorTypes) !== null && _a !== void 0 ? _a : [];
    if (doorTypes.length === 0) {
        return null;
    }
    const title = doorTypes.map(d => doorTypeMap[d]).join(', ');
    return _jsx(InfoRow, { label: "\uCD9C\uC785\uBB38 \uC720\uD615", value: title });
}
export function PlaceDoorDirectionInfoRow({ placeAccessibility, }) {
    const doorDir = placeAccessibility.doorDirectionType;
    if (!doorDir) {
        return null;
    }
    let title = '';
    switch (doorDir) {
        case PlaceDoorDirectionTypeDto.OutsideBuilding:
            title = '건물 밖';
            break;
        case PlaceDoorDirectionTypeDto.InsideBuilding:
            title = '건물 안';
            break;
    }
    return _jsx(InfoRow, { label: "\uCD9C\uC785\uAD6C \uC704\uCE58", value: title });
}
// ──────────────── PlaceNoteInfoRow (특이사항) ────────────────
export function PlaceNoteInfoRow({ placeAccessibility, }) {
    const features = placeAccessibility.features;
    if (!features || features.length === 0) {
        return null;
    }
    return _jsx(InfoRow, { label: "\uD2B9\uC774\uC0AC\uD56D", value: features.join(', ') });
}
// ──────────────── FloorInfoRow ────────────────
export function FloorInfoRow({ accessibility, }) {
    if (!(accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibility)) {
        return null;
    }
    const floorInfo = getFloorAccessibility(accessibility, accessibility.placeAccessibility.doorDirectionType);
    const floorDate = dayjs(accessibility.placeAccessibility.createdAt.value).format('YYYY.MM.DD');
    return (_jsxs(AccessibilitySubSection, { children: [_jsxs(SubSectionHeader, { children: [_jsx(SubSectionTitle, { children: "\uCE35 \uC815\uBCF4" }), _jsx(SubSectionDate, { children: floorDate })] }), _jsx(InfoRowsContainer, { children: _jsx(InfoRow, { label: "\uCE35 \uC815\uBCF4", value: floorInfo.title, subValue: floorInfo.description }) })] }));
}
// ──────────────── StrokeCTAButton (H-48px Stroke + Left icon) ────────────────
export function StrokeCTAButton({ text, onPress, elementName, fullWidth = false, }) {
    return (_jsxs(StrokeCTAContainer, { elementName: elementName, onPress: onPress, style: fullWidth ? { alignSelf: 'stretch' } : undefined, children: [_jsx(PlusStrokeIcon, { width: 20, height: 20, color: color.brand40 }), _jsx(StrokeCTAText, { children: `${text}\u00A0` })] }));
}
// ──────────────── EmptyStateCard ────────────────
export function EmptyStateCard({ title, description, buttonText, onPress, elementName = 'empty_state_card_cta', }) {
    return (_jsxs(EmptyStateCardContainer, { children: [_jsxs(EmptyStateCardTextContainer, { children: [_jsx(EmptyStateCardTitle, { children: title }), _jsx(EmptyStateCardDescription, { children: description })] }), _jsx(StrokeCTAButton, { text: buttonText, onPress: onPress, elementName: elementName, fullWidth: true })] }));
}
// ──────────────── Styled Components ────────────────
// Empty state card
const EmptyStateCardContainer = styled.View `
  background-color: ${color.gray5};
  border-radius: 12px;
  padding: 20px;
  gap: 16px;
  align-items: center;
`;
const EmptyStateCardTitle = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 18px;
  line-height: 26px;
  letter-spacing: -0.36px;
  color: ${color.gray80};
  text-align: center;
`;
const EmptyStateCardTextContainer = styled.View `
  gap: 8px;
`;
const EmptyStateCardDescription = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 15px;
  line-height: 24px;
  letter-spacing: -0.3px;
  color: ${color.gray50};
  text-align: center;
`;
// StrokeCTAButton (Figma H-48px Stroke + Left icon)
// 아이콘 + 텍스트를 별도 row View로 중첩하면 Android에서 row 내부 Text가
// 다음 줄로 깨져 잘렸다 (RN #51306: alignItems:center 부모 + flexDirection:row
// 자식의 Android 너비 측정 버그). 컨테이너 자체를 row 로 평탄화하고 fullWidth는
// align-self:stretch 로 확실한 definite width 를 줘서 텍스트가 intrinsic 너비를
// 온전히 받게 한다 (그래야 측정=렌더가 일치해 잘리지 않음).
const StrokeCTAContainer = styled(SccTouchableOpacity) `
  background-color: ${color.white};
  border-width: 1px;
  border-color: ${color.brand40};
  border-radius: 8px;
  padding-vertical: 12px;
  padding-horizontal: 16px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;
const StrokeCTAText = styled.Text.attrs({
    style: { includeFontPadding: false, textAlignVertical: 'center' },
}) `
  font-family: ${font.pretendardSemibold};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
  color: ${color.brand40};
`;
// Subsection layout
export const AccessibilitySubSection = styled.View `
  gap: 12px;
`;
export const SubSectionHeader = styled.View `
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;
export const SubSectionTitle = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.32px;
  color: ${color.gray80};
`;
export const SubSectionDate = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.24px;
  color: ${color.gray50};
`;
// Info rows
export const InfoRowsContainer = styled.View `
  gap: 12px;
`;
const InfoRowContainer = styled.View `
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
`;
const InfoLabel = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${color.gray50};
  min-height: 26px;
  justify-content: center;
  padding-top: 3px;
`;
const InfoValueContainer = styled.View `
  align-items: flex-end;
  gap: 2px;
`;
const InfoValue = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 16px;
  line-height: 26px;
  letter-spacing: -0.32px;
  color: ${color.gray90};
  text-align: right;
`;
const InfoSubValue = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${color.brand50};
`;
// Photos
const PhotoRowContainer = styled.View `
  flex-direction: row;
  gap: 4px;
`;
const PhotoThumbnail = styled(Image) `
  width: 114px;
  height: 114px;
  border-radius: 8px;
  background-color: #d9d9d9;
`;
// Comment
const CommentContainer = styled.View `
  background-color: ${color.gray5};
  border-radius: 12px;
  padding: 12px;
  gap: 6px;
`;
const CommentText = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.28px;
  color: ${color.gray80};
`;
const CommentMetaRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
const CommentUserName = styled.Text `
  font-family: ${font.pretendardMedium};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.24px;
  color: ${color.brand50};
`;
const CommentDate = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.24px;
  color: ${color.gray60};
`;
// Floor movement empty state
export const FloorMovementEmptyContainer = styled.View `
  background-color: ${color.gray5};
  border-radius: 12px;
  padding: 20px;
  align-items: center;
  justify-content: center;
`;
export const FloorMovementEmptyText = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 14px;
  line-height: 22px;
  letter-spacing: -0.28px;
  color: ${color.gray50};
`;
